import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Handler from '../../../../Handler';
import { GoogleMapBox } from '../../../../components/basic/GoogleMapBox';
import { ContentContext } from '../../../../contexts';
import { useLabel } from '../../../../hooks';

dayjs.extend(utc);

export const BizDetail = ({ reduction }) => {
  const { t, i18n } = useTranslation();
  const { methodologyInfo } = useContext(ContentContext);
  const { TABLE_HEADER } = useLabel();
  const isEn = i18n.language === 'en';

  console.log('reduction', reduction);

  // 프로젝트 데이터 파싱
  const projectData = useMemo(() => {
    if (!reduction?.reductionCert?.project) return null;

    const project = reduction.reductionCert.project;
    const methodology = project?.methodology?.seq
      ? Handler.getMethodologyBySeq(methodologyInfo, project?.methodology?.seq)
      : null;

    return {
      // 프로젝트 정보
      title: isEn ? project.titleEn || project.title : project.title,
      projectId: project.id || '-',
      area: project.methodology?.area || '-',
      subArea: project.methodology?.subArea || '-',
      location: project?.lastDoc?.location || '-',
      methodologySeq: project.methodology?.seq,
      methodologyTitle: isEn ? methodology?.titleEn || methodology?.title : methodology?.title,
      version: project.version || '-',
      startDate: project.startDate ? dayjs(project.startDate).format('YYYY.MM.DD') : '-',
      endDate: project.endDate ? dayjs(project.endDate).format('YYYY.MM.DD') : '-',
      reductionStartDate: project.reductionStartDate ? dayjs(project.reductionStartDate).format('YYYY.MM.DD') : '-',
      reductionEndDate: project.reductionEndDate ? dayjs(project.reductionEndDate).format('YYYY.MM.DD') : '-',
      // 방법론 정보
      methodology: methodology
        ? {
            seq: methodology.seq,
            area: methodology.area || '-',
            subArea: methodology.subArea || '-',
            expiredYear: methodology.expiredYear || '-',
            title: isEn ? methodology.titleEn || methodology.title : methodology.title,
            createdAt: methodology.createdAt ? dayjs(methodology.createdAt * 1000).format('YYYY.MM.DD') : '-',
            // ITS-186. 좌변은 이 화면이 만드는 자체 키라 그대로 두고, 백엔드 응답을 읽는
            // 우변만 새 이름으로 옮긴다.
            termsMapper: methodology.terms || [],
          }
        : null,
    };
  }, [reduction, methodologyInfo, isEn]);

  if (!projectData) {
    return (
      <article className='flex flex-col gap-4 w-[945px] py-8'>
        <header>
          <h2 className='text-2xl font-bold'>{t('CHECK_BUSINESS_SUMMARY')}</h2>
        </header>
        <div className='text-center py-8 text-gray-500'>{t('NO_DATA') || '데이터가 없습니다.'}</div>
      </article>
    );
  }

  return (
    <article className='flex flex-col gap-10 w-[945px] box-border mb-4 py-8'>
      {/* 프로젝트 정보 */}
      <section className='bg-white border border-gray-200 rounded-lg pb-6'>
        <h2 className='font-semibold mb-4 pb-2 border-b border-gray-200'>{t('BIZ_INFO') || '프로젝트 정보'}</h2>
        <div className='flex flex-col gap-4'>
          <div>
            <span className='text-sm text-gray-600'>{t('PROJECT_NAME') || '프로젝트명'}: </span>
            <span className='text-sm font-medium'>{projectData.title}</span>
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('PROJECT_ID') || '프로젝트 ID'}: </span>
              <span className='text-sm font-medium'>{projectData.projectId}</span>
            </div>
            <div>
              <span className='text-sm text-gray-600'>{t('VERSION') || '버전'}: </span>
              <span className='text-sm font-medium'>{projectData.version}</span>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('START_DATE') || '시작일'}: </span>
              <span className='text-sm font-medium'>{projectData.startDate}</span>
            </div>
            <div>
              <span className='text-sm text-gray-600'>{t('END_DATE') || '종료일'}: </span>
              <span className='text-sm font-medium'>{projectData.endDate}</span>
            </div>
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('BIZ_SITE') || '사업시행 장소'}: </span>
              <span className='text-sm font-medium'>
                {Handler.jsonParser(projectData.location)?.addr1?.formatted_address}
                {Handler.jsonParser(projectData.location)?.addr2}
              </span>
            </div>
          </div>
          <div>
            <GoogleMapBox
              initialAddress={Handler.jsonParser(projectData.location)?.addr1?.formatted_address}
              size={{ width: '100%', height: '300px' }}
            />
          </div>
        </div>
      </section>

      {/* 방법론 정보 */}
      {projectData.methodology && (
        <section className='bg-white pt-12 border-t border-solid border-slate-200'>
          <h2 className='font-semibold mb-4 pb-2 border-b border-gray-200'>{t('METHODOLOGY')}</h2>
          <div className='flex flex-col gap-4'>
            <div>
              <span className='text-sm text-gray-600'>{t('METHODOLOGY')}: </span>
              <span className='text-sm font-medium'>{projectData.methodology.title}</span>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <span className='text-sm text-gray-600'>{t('AREA')}: </span>
                <span className='text-sm font-medium'>
                  {t(projectData.methodology.area?.toUpperCase()) || projectData.methodology.area}
                </span>
              </div>
              <div>
                <span className='text-sm text-gray-600'>{t('SUB_AREA')}: </span>
                <span className='text-sm font-medium'>
                  {t(projectData.methodology.subArea?.toUpperCase()) || projectData.methodology.subArea}
                </span>
              </div>
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <span className='text-sm text-gray-600'>{t('EXPIRE_DATE') || '만료 연도'}: </span>
                <span className='text-sm font-medium'>{projectData.methodology.expiredYear}Y</span>
              </div>
              <div>
                <span className='text-sm text-gray-600'>{TABLE_HEADER['CREATED_DATE']}: </span>
                <span className='text-sm font-medium'>{projectData.methodology.createdAt}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {}
    </article>
  );
};
